import { Feature, MultiPolygon, Polygon } from '@turf/turf';
import { ILAANCAdvisoryProperties, ILAANCBlockReason } from '../advisories/ILAANCAdvisoryProperties';
import { ICompositeLAANCAdvisoryProperties } from '../advisories/ICompositeLAANCAdvisoryProperties';
import { ITwoCharacterAirspaceClassification } from '../authority-tiles/IAirspaceProperties';
import { ILAANCEanbledFor } from '../authority-tiles/ILAANCProperties';
import { MissionFeatureCollectionFacade } from '../MissionFeatureCollectionFacade';

export class CompositeLAANCAdvisoryProvider {
    getCompositeAdvisory = (mission: MissionFeatureCollectionFacade): ICompositeLAANCAdvisoryProperties | undefined => {
        const laancAdvisories = mission.features.filter(f => f.properties.source === 'uasfm+airspace') as Feature<Polygon | MultiPolygon, ILAANCAdvisoryProperties>[];

        const laancAuthorizations = mission.getLAANCAuthorizeableAdvisoryFeatures();
        const endTime = mission.getMissionRadius().properties.endTime;
        const someInitialized = laancAuthorizations.some(a => a.properties.getAPLAANCStatus({ endTime }) === 'initialized');
        const someNotInitialized = laancAuthorizations.some(a => a.properties.getAPLAANCStatus({ endTime }) !== 'initialized');

        // only return the composite if we have some some laanc authorizations (either now or did in the past)
        if (laancAdvisories.length + laancAuthorizations.length === 0){
            return undefined;
        }

        // we could have authorizations but no advisories (because LAANC was required when we
        // got our authorizations, but is no longer required). Currently we want to treat that
        // as blocked.
        if (laancAuthorizations.length > 0 && laancAdvisories.length === 0){
            return this.buildBlockedBecauseNoLongerLAANC(mission);
        }

        // or we could have new authorizations, effectively requiring us to block the mission
        // since we don't support authorizing them after mission creation (yet)
        if(someInitialized && someNotInitialized){
            return this.buildBlockedBecausePreviousLAANCMissionHasNewAuthorizations(mission);
        }

        const someInitializedByFAASourceChange = laancAuthorizations.some(a => ['initialized', 'never-authorized'].includes(a.properties.getAPLAANCStatus({ endTime })) && a.properties.getLAANCStatusHistory()[0].statusMeta.initializedSource === 'faa-gis-change');
        if (someInitializedByFAASourceChange){
            return this.buildBlockedBecausePreviousNonLAANCMissionHasNewAuthorizations(mission);
        }

        return this.buildNormalComposite(mission);
    }

    private buildNormalComposite = (mission: MissionFeatureCollectionFacade): ICompositeLAANCAdvisoryProperties => {
        const laancAdvisories = mission.features.filter(f => f.properties.source === 'uasfm+airspace') as Feature<Polygon | MultiPolygon, ILAANCAdvisoryProperties>[];
        let anyLAANCBlock = laancAdvisories.some(f => f.properties.advisoryType === 'block');
        const blockReasons: ILAANCBlockReason[] = laancAdvisories.reduce((reasons: ILAANCBlockReason[], f) => reasons.concat(f.properties!.blockReasons), [])
            .reduce(uniqueReducer, []) as ILAANCBlockReason[];
        const CEILING = Math.min(...laancAdvisories.map(f => f.properties.CEILING));

        const UASFM_Enabled: ILAANCEanbledFor[] = this.getUASFM_Enabled(laancAdvisories);
        // maybe that just blocked it (eg there was a one uasfm with FC and another uasfm with AA)
        // making the composite not AA
        if (UASFM_Enabled.length === 0) {
            if (!blockReasons.includes('laanc-not-aa')) {
                blockReasons.push('laanc-not-aa');
            }
            anyLAANCBlock = true;
        }

        const laancAuthorizations = mission.getLAANCAuthorizeableAdvisoryFeatures();

        return {
            advisoryType: anyLAANCBlock ? 'block' : 'advise',
            CEILING,
            source: 'uasfm+airspace+composite',
            blockReasons,
            AP_CLASSES: laancAdvisories.map(f => f.properties.AP_CLASS).reduce(uniqueReducer, []) as ITwoCharacterAirspaceClassification[],
            UASFM_Enabled,
            apLAANCStatus: laancAuthorizations.length > 0 ? mission.getAPLAANCStatus() : undefined
        };
    }

    private buildBlockedBecausePreviousLAANCMissionHasNewAuthorizations = (mission: MissionFeatureCollectionFacade): ICompositeLAANCAdvisoryProperties => {
        const composite = this.buildNormalComposite(mission);
        composite.advisoryType = 'block';

        // this isn't really the reason, but good enough for now
        // the real investment here should be to enable the user to get the authorizations
        // that they now need
        composite.blockReasons = ['laanc-inactive'];

        return composite;
    }

    private buildBlockedBecausePreviousNonLAANCMissionHasNewAuthorizations = (mission: MissionFeatureCollectionFacade): ICompositeLAANCAdvisoryProperties => {
        const laancAdvisories = mission.features.filter(f => f.properties.source === 'uasfm+airspace') as Feature<Polygon | MultiPolygon, ILAANCAdvisoryProperties>[];
        const UASFM_Enabled: ILAANCEanbledFor[] = this.getUASFM_Enabled(laancAdvisories);
        return {
            advisoryType: 'block',

            // this isn't the real ceiling, but we're blocking the mission so it doesn't matter
            CEILING: 0,

            source: 'uasfm+airspace+composite',

            // this isn't really the reason, but good enough for now
            // the real investment here should be to enable the user to get the authorizations
            // that they now need
            blockReasons: ['laanc-inactive'],

            AP_CLASSES: laancAdvisories.map(f => f.properties.AP_CLASS).reduce(uniqueReducer, []) as ITwoCharacterAirspaceClassification[],
            UASFM_Enabled,
            apLAANCStatus: mission.getAPLAANCStatus()
        };
    }

    private buildBlockedBecauseNoLongerLAANC = (mission: MissionFeatureCollectionFacade): ICompositeLAANCAdvisoryProperties => {
        return {
            advisoryType: 'block',
            source: 'uasfm+airspace+composite',

            // this isn't really the reason, but good enough for now
            // the real investment here should be to enable the user to cancel the authorizations
            // that they no longer need
            blockReasons: ['laanc-inactive'],

            // they are technically allowed to fly, but we need to pretend like it is blocked, so 0
            CEILING: 0,

            // we don't know (b/c we don't have advisories)
            AP_CLASSES: [],
            UASFM_Enabled: [], // we could get them from the authorizations but why bother
            apLAANCStatus: mission.getAPLAANCStatus()
        };
    }

    private getUASFM_Enabled = (laancAdvisories: Feature<Polygon | MultiPolygon, ILAANCAdvisoryProperties>[]): ILAANCEanbledFor[] => {
        const doAllAllow107AA = laancAdvisories.every(f => f.properties.UASFM_Enabled.includes('107-AA'));
        const doAllAllow107FC = laancAdvisories.every(f => f.properties.UASFM_Enabled.includes('107-FC'));
        const doAllAllow44809FC = laancAdvisories.every(f => f.properties.UASFM_Enabled.includes('44809-AA'));

        const response: ILAANCEanbledFor[] = [];
        if (doAllAllow107AA) {
            response.push('107-AA');
        }
        if (doAllAllow107FC) {
            response.push('107-FC');
        }
        if (doAllAllow44809FC) {
            response.push('44809-AA');
        }

        return response;
    }
}

// TODO: DRY this out
const uniqueReducer = (acc: string[], item: string): string[] => {
    if (!acc.includes(item)) {
        acc.push(item);
    }
    return acc;
};
